/**
 * @file     accountFlagsClient.ts
 * @purpose  Client helpers that write server-owned account flags through the
 *           CSRF-protected routes (never supabase.auth.updateUser, which writes
 *           client-writable user_metadata).
 */
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { VOW_TOU_VERSION } from '@/services/vow/vowTerms';

// Fire-and-forget: these markers drive UX (banners, recovery links), so a
// transient failure must never block the auth happy-path.
export async function markPasswordSet(surface?: string): Promise<void> {
  try {
    const res = await secureFetch('/api/auth/account-flags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flag: 'password_set', ...(surface ? { surface } : {}) }),
    });
    if (!res.ok) return;
    // app_metadata rides in the access token, so the browser keeps reading the
    // pre-flag JWT for the rest of its hour. Every password_set reader (the
    // dashboard set-password banner, the post-gate prompt, the product tour)
    // then still sees a passwordless account and asks for a password the user
    // just chose — and each time they comply, Supabase sends another
    // "your password has been changed" email. Refresh so the flag is visible now.
    const { getSupabaseBrowserClient } = await import('@/core/supabase/client');
    const supabase = await getSupabaseBrowserClient();
    await supabase?.auth.refreshSession();
  } catch {
    /* non-fatal */
  }
}

export async function markEmailSelfVerified(): Promise<void> {
  try {
    await secureFetch('/api/auth/account-flags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flag: 'email_self_verified' }),
    });
  } catch {
    /* non-fatal */
  }
}

export interface VowRegisterResult {
  ok: boolean;
  error?: string;
  /** The account already proved this number; false means ask for a code. Absent on older responses. */
  phoneVerified?: boolean;
}

export async function registerVow(input: { mode: 'activate' | 'reactivate'; phone?: string }): Promise<VowRegisterResult> {
  try {
    // The activate call asserts the terms the user was shown (ADR-025). Only
    // the client knows it actually rendered them, so it says so explicitly and
    // names the version; the server rejects anything else.
    const payload =
      input.mode === 'activate'
        ? { ...input, touAccepted: true, touVersion: VOW_TOU_VERSION }
        : input;
    const res = await secureFetch('/api/auth/vow-register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: data?.error ?? 'Something went wrong. Please try again.' };
    return { ok: true, phoneVerified: typeof data?.phoneVerified === 'boolean' ? data.phoneVerified : undefined };
  } catch {
    return { ok: false, error: 'Something went wrong. Please try again.' };
  }
}
